import { buildTppFulfillmentRowsForCategory } from './pptTPPhelpers'
import { getOrderedValueMessages, resolveActivityTimelineDates } from './pptExportHelpers'

export async function fetchActivitiesForExport({ callSecureAPI, endpoints, strategyId, token }) {
    const resp = await callSecureAPI(
        endpoints.getActivitiesForTable.url + strategyId,
        endpoints.getActivitiesForTable.method,
        token
    )

    return resp?.success && Array.isArray(resp.data) ? resp.data : []
}

export function buildActivityById(activities = []) {
    return activities.reduce((acc, activity) => {
        const id = activity?._id || activity?.id

        if (id) {
            acc[id] = activity
        }

        return acc
    }, {})
}

export function buildValueMessageNameMap(valueMessages = []) {
    const map = {}

    const addVm = (vm) => {
        const id = vm?._id || vm?.id
        if (!id) return

        map[String(id)] = vm?.name ?? vm?.text ?? vm?.title ?? ''
    }

    for (const item of valueMessages || []) {
        addVm(item)

        // defensive support if endpoint returns grouped/nested data later
        if (Array.isArray(item?.valueMessages)) {
            item.valueMessages.forEach(addVm)
        }

        if (Array.isArray(item?.valueNarratives)) {
            item.valueNarratives.forEach((vn) => {
                ;(vn?.valueMessages || []).forEach(addVm)
            })
        }
    }

    return map
}

export function buildKesByEvidenceId(evidenceRows = []) {
    const map = new Map()

    for (const ev of evidenceRows || []) {
        const evidenceId = ev?._id || ev?.id

        if (!evidenceId) continue

        const kes = Array.isArray(ev?.keyEvidenceStatements) ? ev.keyEvidenceStatements : []

        map.set(evidenceId, kes)
    }

    return map
}

export async function fetchValueMessagesForExport({ callSecureAPI, endpoints, strategyId, token }) {
    const resp = await callSecureAPI(
        endpoints.getValueMessagesForTable.url + strategyId,
        endpoints.getValueMessagesForTable.method,
        token
    )

    return resp?.success && Array.isArray(resp.data) ? resp.data : []
}

export async function fetchEvidenceForExport({ callSecureAPI, endpoints, strategyId, token }) {
    const resp = await callSecureAPI(
        endpoints.getEvidenceForTable.url + strategyId,
        endpoints.getEvidenceForTable.method,
        token
    )

    return resp?.success && Array.isArray(resp.data) ? resp.data : []
}

export async function fetchValueNarrativesForExport({ callSecureAPI, endpoints, strategyId, token }) {
    const resp = await callSecureAPI(
        endpoints.getValueCompendiumValueNarratives.url + strategyId,
        endpoints.getValueCompendiumValueNarratives.method,
        token
    )

    return resp?.success && Array.isArray(resp.data) ? resp.data : []
}

export async function fetchStrategicImperativesForExport({ callSecureAPI, endpoints, strategyId, token }) {
    const resp = await callSecureAPI(endpoints.getSIForTable.url + strategyId, endpoints.getSIForTable.method, token)

    return resp?.success && Array.isArray(resp.data) ? resp.data : []
}

export function buildStrategicImperativesByActivityId(strategicImperatives = []) {
    const map = {}

    for (const si of strategicImperatives || []) {
        const linkedActivities = Array.isArray(si?.linkedActivities) ? si.linkedActivities : []

        for (const activityId of linkedActivities) {
            const key = String(activityId)

            if (!map[key]) map[key] = []

            map[key].push({
                name: si?.name ?? '',
                category: si?.category ?? '',
                description: si?.description ?? '',
            })
        }
    }

    return map
}

export async function fetchDisseminationPrioritizationExportData({ callSecureAPI, endpoints, strategyId, token }) {
    const [activities, valueMessages, evidenceRows, valueNarratives] = await Promise.all([
        fetchActivitiesForExport({ callSecureAPI, endpoints, strategyId, token }),
        fetchValueMessagesForExport({ callSecureAPI, endpoints, strategyId, token }),
        fetchEvidenceForExport({ callSecureAPI, endpoints, strategyId, token }),
        fetchValueNarrativesForExport({ callSecureAPI, endpoints, strategyId, token }),
    ])

    const evidenceGapsByVmId = {}

    const nestedVms =
        valueNarratives?.flatMap((category) =>
            (category?.valueNarratives || []).flatMap((narrative) => narrative?.valueMessages || [])
        ) || []

    for (const vm of nestedVms) {
        try {
            const gapResponse = await callSecureAPI(
                endpoints.getEvidenceGapsByValueMessageId.url + vm._id,
                endpoints.getEvidenceGapsByValueMessageId.method,
                token
            )

            evidenceGapsByVmId[String(vm._id)] = gapResponse?.data || []
        } catch (err) {
            console.error('Failed to fetch evidence gaps for VM', vm?._id, err)

            evidenceGapsByVmId[String(vm._id)] = []
        }
    }

    const model = buildDisseminationPrioritizationModel({
        activities,
        valueMessages,
        evidenceRows,
        valueNarratives,
        evidenceGapsByVmId,
    })

    return model

    /*return {
        activities,
        valueMessages,
        evidenceRows,
        valueNarratives,
    }

     */
}

export function buildDisseminationPrioritizationModel({
    activities = [],
    valueMessages = [],
    evidenceRows = [],
    valueNarratives = [],
    evidenceGapsByVmId = {},
}) {
    const flatValueMessages = flattenDisseminationValueMessages(valueMessages, valueNarratives)
    const activitiesByVmId = buildDisseminationActivitiesByVmId(activities)

    const externalAudiences = collectDisseminationAudiences(flatValueMessages, 'external')
    const internalAudiences = collectDisseminationAudiences(flatValueMessages, 'internal')

    const rows = []

    for (const category of valueNarratives || []) {
        const categoryName = category?.name ?? category?.category ?? category?.pillar ?? ''

        for (const narrative of category?.valueNarratives || []) {
            const archiveValue = narrative?.archived ?? narrative?.valueMessages?.[0]?.narrative?.archived

            if (archiveValue != null) continue

            const narrativeVms = getOrderedValueMessages(narrative?.valueMessages)

            rows.push({
                rowType: 'valueNarrative',
                category: categoryName,
                label: narrative?.name ?? narrative?.title ?? narrative?.description ?? '',
                references: getDisseminationNarrativeReferencesFromVms(narrativeVms),
            })

            for (const vm of narrativeVms) {
                addDisseminationVmRows({
                    rows,
                    vm,
                    category: '',
                    activitiesByVmId,
                    evidenceGapsByVmId,
                    externalAudiences,
                    internalAudiences,
                })
            }
        }
    }

    return {
        rows,
        externalAudiences,
        internalAudiences,
        activities,
        valueMessages: flatValueMessages,
        evidenceRows,
        valueNarratives,
        references: buildDisseminationAllReferencesFromNestedValueNarratives(valueNarratives),
    }
}

function flattenDisseminationValueMessages(valueMessages = [], valueNarratives = []) {
    const map = new Map()

    const addVm = (vm) => {
        const id = vm?._id || vm?.id
        if (!id) return
        map.set(String(id), vm)
    }

    ;(valueMessages || []).forEach(addVm)

    for (const category of valueNarratives || []) {
        for (const narrative of category?.valueNarratives || []) {
            ;(narrative?.valueMessages || []).forEach(addVm)
        }
    }

    return Array.from(map.values())
}

function getDisseminationVmsForNarrative(narrative = {}, flatValueMessages = []) {
    const embedded = Array.isArray(narrative?.valueMessages) ? narrative.valueMessages : []
    if (embedded.length) return embedded

    const narrativeId = String(narrative?._id || narrative?.id || '')

    return flatValueMessages.filter((vm) => {
        const vmNarrativeId =
            vm?.valueNarrativeId ??
            vm?.valueNarrative ??
            vm?.narrativeId ??
            vm?.parentNarrativeId ??
            vm?.valueNarrative?._id ??
            vm?.valueNarrative?.id

        return String(vmNarrativeId || '') === narrativeId
    })
}

export function buildEvidenceById(evidence = []) {
    return (Array.isArray(evidence) ? evidence : []).reduce((acc, ev) => {
        const id = ev?._id || ev?.id

        if (id) {
            acc[String(id)] = ev
        }

        return acc
    }, {})
}

function addDisseminationVmRows({
    rows,
    vm,
    category,
    activitiesByVmId,
    evidenceGapsByVmId = {},
    externalAudiences,
    internalAudiences,
}) {
    const vmId = String(vm?._id || vm?.id || '')
    const vmActivities = [
        ...(activitiesByVmId[vmId] || []),
        ...(Array.isArray(vm?.linkedActivities) ? vm.linkedActivities : []),
    ]
    const activityBuckets = bucketDisseminationActivitiesByStatus(vmActivities)

    rows.push({
        rowType: 'valueMessage',
        category,
        label: vm?.name ?? vm?.text ?? vm?.title ?? '',
        status: vm?.status ?? '',
        priority: vm?.priority ?? '',
        externalScores: buildDisseminationAudienceScores(vm, externalAudiences, 'external'),
        internalScores: buildDisseminationAudienceScores(vm, internalAudiences, 'internal'),
        references: getDisseminationVmReferencesFromVm(vm),
        evidenceUnderDevelopment: activityBuckets.underDevelopmentActivities?.length > 0 ? 'Y' : '-',
        evidenceUnderConsideration: activityBuckets.underConsiderationActivities?.length > 0 ? 'Y' : '-',
        ...activityBuckets,
    })

    rows.push(...buildDisseminationKesRowsFromVm(vm))

    const evidenceGaps = evidenceGapsByVmId[vmId] || getDisseminationEvidenceGaps(vm)

    for (const gap of evidenceGaps) {
        rows.push({
            rowType: 'evidenceGap',
            category: '',
            label: typeof gap === 'string' ? gap : (gap?.name ?? gap?.text ?? gap?.description ?? ''),
            references: [],
            evidenceGenerationPlan: '-',
        })
    }
}

function collectDisseminationAudiences(valueMessages = [], type) {
    const names = new Set()

    for (const vm of valueMessages || []) {
        const audiences = getDisseminationAudienceArray(vm, type)

        for (const audience of Array.isArray(audiences) ? audiences : []) {
            const name =
                typeof audience === 'string'
                    ? audience
                    : (audience?.name ?? audience?.label ?? audience?.audience ?? audience?.type ?? '')

            if (name) names.add(name)
        }
    }

    return Array.from(names)
}

function getDisseminationAudienceArray(vm = {}, type) {
    const raw =
        type === 'external'
            ? (vm?.audiences ?? vm?.externalStakeholders ?? vm?.externalAudiences ?? {})
            : (vm?.internalStakeholders ?? vm?.internalStakeholder ?? vm?.stakeholders ?? {})

    if (!raw) return []

    if (Array.isArray(raw)) {
        return raw
            .map((item) => {
                if (typeof item === 'string') return item
                return item?.name ?? item?.label ?? item?.stakeholder ?? item?.audience ?? item?.type ?? ''
            })
            .filter(Boolean)
    }

    if (typeof raw === 'string') {
        return raw
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
    }

    if (typeof raw === 'object') {
        return Object.entries(raw)
            .filter(([, value]) => isDisseminationAudienceValueSelected(value))
            .map(([key, value]) => {
                if (value && typeof value === 'object') {
                    return value?.name ?? value?.label ?? value?.stakeholder ?? value?.audience ?? key
                }

                return key
            })
            .filter(Boolean)
    }

    return []
}

function buildDisseminationAudienceScores(vm, audienceNames, type) {
    const selected = getDisseminationAudienceArray(vm, type)

    return audienceNames.reduce((acc, audienceName) => {
        acc[audienceName] = isDisseminationAudienceSelected(selected, audienceName) ? 10 : 0
        return acc
    }, {})
}

function isDisseminationAudienceSelected(selected = [], audienceName) {
    return (selected || []).some((audience) => {
        if (typeof audience === 'string') return audience === audienceName

        const name = audience?.name ?? audience?.label ?? audience?.audience ?? audience?.type ?? ''

        const value = audience?.selected ?? audience?.checked ?? audience?.included ?? audience?.value ?? true

        return name === audienceName && value !== false
    })
}

function getDisseminationVmEvidenceIds(vm = {}) {
    return [
        ...(Array.isArray(vm?.linkedEvidence) ? vm.linkedEvidence : []),
        ...(Array.isArray(vm?.evidence) ? vm.evidence : []),
        ...(Array.isArray(vm?.evidenceIds) ? vm.evidenceIds : []),
        ...(Array.isArray(vm?.parentEvidence) ? vm.parentEvidence : []),
    ].map((item) => String(item?._id || item?.id || item))
}

function getDisseminationVmReferences(vm = {}, evidenceById = {}) {
    return dedupeDisseminationStrings(
        getDisseminationVmEvidenceIds(vm)
            .map((id) => evidenceById[String(id)])
            .filter(Boolean)
            .map(normalizeDisseminationCitation)
            .filter(Boolean)
    )
}

function getDisseminationNarrativeReferences(narrativeVms = [], evidenceById = {}) {
    return dedupeDisseminationStrings(narrativeVms.flatMap((vm) => getDisseminationVmReferences(vm, evidenceById)))
}

function buildDisseminationKesRowsForVm(vm = {}, evidenceById = {}) {
    const kesMap = new Map()

    for (const evidenceId of getDisseminationVmEvidenceIds(vm)) {
        const evidence = evidenceById[String(evidenceId)]
        if (!evidence) continue

        const citation = normalizeDisseminationCitation(evidence)
        const kesList = Array.isArray(evidence?.keyEvidenceStatements) ? evidence.keyEvidenceStatements : []

        for (const kes of kesList) {
            const text = typeof kes === 'string' ? kes : (kes?.statement ?? kes?.text ?? kes?.name ?? '')

            const key = text.trim().toLowerCase()
            if (!key) continue

            if (!kesMap.has(key)) {
                kesMap.set(key, {
                    rowType: 'kes',
                    category: '',
                    label: text,
                    references: [],
                    evidenceGenerationPlan: '-',
                })
            }

            if (citation && !kesMap.get(key).references.includes(citation)) {
                kesMap.get(key).references.push(citation)
            }
        }
    }

    return Array.from(kesMap.values())
}

function getDisseminationEvidenceGaps(vm = {}) {
    return vm?.evidenceGaps ?? vm?.linkedEvidenceGaps ?? vm?.gaps ?? []
}

function buildDisseminationActivitiesByVmId(activities = []) {
    const map = {}

    for (const activity of activities || []) {
        const linkedVms = [
            ...(Array.isArray(activity?.linkedValueMessages) ? activity.linkedValueMessages : []),
            ...(Array.isArray(activity?.valueMessages) ? activity.valueMessages : []),
            ...(Array.isArray(activity?.linkedValueMessageIds) ? activity.linkedValueMessageIds : []),
        ]

        for (const vm of linkedVms) {
            const key = String(vm?._id || vm?.id || vm)

            if (!map[key]) map[key] = []

            map[key].push(activity)
        }
    }

    return map
}

function bucketDisseminationActivitiesByStatus(activities = []) {
    const buckets = {
        completedActivities: [],
        ongoingActivities: [],
        plannedActivities: [],
        suspendedActivities: [],
        underDevelopmentActivities: [],
        underConsiderationActivities: [],
    }

    for (const activity of activities || []) {
        const statusGroup = normalizeActivityEvidenceSignal(activity?.status)
        const title = activity?.title ?? activity?.name ?? activity?.activityTitle ?? ''

        if (!title) continue

        if (statusGroup === 'completed') buckets.completedActivities.push(title)
        if (statusGroup === 'underDevelopment') buckets.underDevelopmentActivities.push(title)
        if (statusGroup === 'underConsideration') buckets.underConsiderationActivities.push(title)
        if (statusGroup === 'suspended') buckets.suspendedActivities.push(title)

        if (statusGroup === 'underDevelopment') {
            buckets.ongoingActivities.push(title)
        }

        if (statusGroup === 'underConsideration') {
            buckets.plannedActivities.push(title)
        }
    }

    return buckets
}

function normalizeActivityEvidenceSignal(status = '') {
    const normalized = String(status || '').toLowerCase()

    if (
        normalized.includes('ongoing') ||
        normalized.includes('active') ||
        normalized.includes('initiated') ||
        normalized.includes('in process') ||
        normalized.includes('planned')
    ) {
        return 'underDevelopment'
    }

    if (
        normalized.includes('proposed') ||
        normalized.includes('ideation') ||
        normalized.includes('concept') ||
        normalized.includes('consideration')
    ) {
        return 'underConsideration'
    }

    if (normalized.includes('complete')) {
        return 'completed'
    }

    if (normalized.includes('hold') || normalized.includes('cancel') || normalized.includes('terminat')) {
        return 'suspended'
    }

    return 'other'
}

function normalizeDisseminationCitation(evidence = {}) {
    return evidence?.citation ?? evidence?.reference ?? evidence?.title ?? ''
}

function buildDisseminationAllReferences(evidenceRows = []) {
    return dedupeDisseminationStrings((evidenceRows || []).map(normalizeDisseminationCitation).filter(Boolean))
}

function dedupeDisseminationStrings(values = []) {
    return Array.from(new Set(values.filter(Boolean)))
}

function getDisseminationVmReferencesFromVm(vm = {}) {
    const references = []

    for (const ev of vm?.linkedEvidence || []) {
        const citation = normalizeDisseminationCitation(ev)
        if (citation) references.push(citation)
    }

    for (const ev of vm?.primaryEvidence || []) {
        const citation = normalizeDisseminationCitation(ev)
        if (citation) references.push(citation)
    }

    for (const kes of vm?.keyEvidenceStatements || []) {
        const citation = kes?.parentEvidence?.citation ?? kes?.citation ?? ''
        if (citation) references.push(citation)
    }

    return dedupeDisseminationStrings(references)
}

function getDisseminationNarrativeReferencesFromVms(valueMessages = []) {
    return dedupeDisseminationStrings((valueMessages || []).flatMap((vm) => getDisseminationVmReferencesFromVm(vm)))
}

function buildDisseminationKesRowsFromVm(vm = {}) {
    const kesMap = new Map()
    const kesList = Array.isArray(vm?.keyEvidenceStatements) ? vm.keyEvidenceStatements : []

    for (const kes of kesList) {
        const text = typeof kes === 'string' ? '' : (kes?.statement ?? kes?.text ?? kes?.name ?? '')

        const citation = kes?.parentEvidence?.citation ?? kes?.citation ?? ''

        const key = text.trim().toLowerCase()
        if (!key) continue

        if (!kesMap.has(key)) {
            kesMap.set(key, {
                rowType: 'kes',
                category: '',
                label: text,
                references: [],
                evidenceGenerationPlan: '-',
            })
        }

        if (citation && !kesMap.get(key).references.includes(citation)) {
            kesMap.get(key).references.push(citation)
        }
    }

    const linkedEvidence = [
        ...(Array.isArray(vm?.linkedEvidence) ? vm.linkedEvidence : []),
        ...(Array.isArray(vm?.primaryEvidence) ? vm.primaryEvidence : []),
    ]

    if (kesMap.size === 0 && linkedEvidence.length > 0) {
        const references = dedupeDisseminationStrings(
            linkedEvidence.map(normalizeDisseminationCitation).filter(Boolean)
        )

        return [
            {
                rowType: 'kes',
                category: '',
                label: 'Needs KES',
                references,
                evidenceGenerationPlan: '-',
            },
        ]
    }

    return Array.from(kesMap.values())
}

function buildDisseminationAllReferencesFromNestedValueNarratives(valueNarratives = []) {
    const references = []

    for (const category of valueNarratives || []) {
        for (const narrative of category?.valueNarratives || []) {
            for (const vm of narrative?.valueMessages || []) {
                references.push(...getDisseminationVmReferencesFromVm(vm))
            }
        }
    }

    return dedupeDisseminationStrings(references)
}

function isDisseminationAudienceValueSelected(value) {
    if (value === true) return true
    if (value === 1) return true
    if (value === '1') return true
    if (value === 'true') return true
    if (value === 'True') return true
    if (value === 'yes') return true
    if (value === 'Yes') return true
    if (value === 'Y') return true

    if (value && typeof value === 'object') {
        return (
            value?.selected === true ||
            value?.checked === true ||
            value?.included === true ||
            value?.value === true ||
            value?.enabled === true
        )
    }

    return false
}

export async function fetchEvidenceGapsForExport({ callSecureAPI, endpoints, strategyId, token }) {
    const resp = await callSecureAPI(
        endpoints.getEvidenceGapsForTable.url + strategyId,
        endpoints.getEvidenceGapsForTable.method,
        token
    )

    return resp?.success && Array.isArray(resp.data) ? resp.data : []
}

export function buildValueMessageFulfillmentGanttGroups({ valueNarratives = [], activities = [] }) {
    const activityById = buildActivityById(activities)

    return (valueNarratives || [])
        .map((category) => {
            const categoryName = category?.name ?? category?.category ?? category?.pillar ?? 'Uncategorized'

            const activityMap = new Map()

            for (const narrative of category?.valueNarratives || []) {
                for (const vm of narrative?.valueMessages || []) {
                    const linkedActivities = Array.isArray(vm?.linkedActivities) ? vm.linkedActivities : []

                    for (const linkedActivity of linkedActivities) {
                        const activityId = String(linkedActivity?._id || linkedActivity?.id || linkedActivity)

                        const fullActivity = activityById[activityId]

                        const activity =
                            typeof linkedActivity === 'object'
                                ? {
                                      ...linkedActivity,
                                      ...fullActivity,
                                  }
                                : fullActivity

                        if (!activity) continue

                        const dedupeId = String(activity?._id || activity?.id || activityId)

                        if (!activityMap.has(dedupeId)) {
                            const timelineDates = resolveActivityTimelineDates(activity)

                            activityMap.set(dedupeId, {
                                ...activity,

                                _ganttStartDate: timelineDates.resolvedStartDate,
                                _ganttEndDate: timelineDates.resolvedEndDate,
                                _ganttTimeline: timelineDates,

                                _ganttCategory: categoryName,
                                _linkedValueMessages: [],
                            })
                        }

                        activityMap.get(dedupeId)._linkedValueMessages.push({
                            id: vm?._id || vm?.id || '',
                            name: vm?.name ?? vm?.text ?? vm?.title ?? '',
                            narrative: narrative?.name ?? narrative?.title ?? '',
                            category: categoryName,
                        })
                    }
                }
            }

            return {
                category: categoryName,
                activities: Array.from(activityMap.values()),
            }
        })
        .filter((group) => group.activities.length > 0)
}

export function buildTppFulfillmentGanttGroups({
    categories = [],
    baseScenarioId,
    bestScenarioId,
    activityMap = {},
    allActivities = [],
}) {
    const getActivityKey = (activity) =>
        String(activity?._id || activity?.id || activity?.activityId || activity?.studyId || activity?.title || '')

    const fullActivityById = new Map(
        (allActivities || []).map((activity) => [getActivityKey(activity), activity]).filter(([key]) => key)
    )

    const mergeActivity = (activity) => {
        const key = getActivityKey(activity)
        if (!key) return null

        const fullActivity = fullActivityById.get(key)

        return {
            ...activity,
            ...(fullActivity || {}),
        }
    }

    const addActivityToMap = (activityById, activity, scenarioKey) => {
        const key = getActivityKey(activity)
        if (!key) return

        const mergedActivity = mergeActivity(activity)
        if (!mergedActivity) return

        if (!activityById.has(key)) {
            const timelineDates = resolveActivityTimelineDates(mergedActivity)

            activityById.set(key, {
                ...mergedActivity,

                _ganttStartDate: timelineDates.resolvedStartDate,

                _ganttEndDate: timelineDates.resolvedEndDate,

                _ganttTimeline: timelineDates,

                _tppFulfillmentScenarios: new Set(),
            })
        }

        activityById.get(key)._tppFulfillmentScenarios.add(scenarioKey)
    }

    return (categories || [])
        .map((category) => {
            const categoryName = category?.name || category?.category_name || 'Uncategorized'
            const activityById = new Map()

            const fulfillmentRows = buildTppFulfillmentRowsForCategory(
                category,
                baseScenarioId,
                bestScenarioId,
                activityMap
            )

            fulfillmentRows.forEach((row) => {
                ;(row.baseActivities || []).forEach((activity) => {
                    addActivityToMap(activityById, activity, 'base')
                })
                ;(row.bestActivities || []).forEach((activity) => {
                    addActivityToMap(activityById, activity, 'best')
                })
            })

            return {
                category: categoryName,
                activities: Array.from(activityById.values()).map((activity) => ({
                    ...activity,
                    _tppFulfillmentScenarios: Array.from(activity._tppFulfillmentScenarios || []),
                })),
            }
        })
        .filter((group) => group.activities.length > 0)
}
